import { ListTodo, Pencil, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader } from "@/components/common/loader";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { deleteCaseStep, listClientCases, updateCaseStep } from "@/features/cases/api";
import { TaskFormDialog } from "@/features/cases/components/task-form-dialog";
import type { Case, CaseStep } from "@/features/cases/types";

/** Which task the create/edit dialog is targeting. */
type DialogTarget = { caseId: string; step: CaseStep | null; nextPosition: number };

/**
 * Full task management for a client: every case's workflow steps shown as a
 * todo list with create / edit / toggle-complete / delete. Tasks are
 * case-scoped in this app, so each case is its own group and new tasks are
 * added under a specific case. With no cases the empty state points to Cases.
 */
export function TasksSection({ clientId }: { clientId: string }) {
  const [cases, setCases] = useState<Case[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState<DialogTarget | null>(null);
  const [deleting, setDeleting] = useState<{ caseId: string; step: CaseStep } | null>(null);

  useEffect(() => {
    let active = true;
    listClientCases(clientId)
      .then((result) => active && setCases(result))
      .catch(() => active && toast.error("Couldn't load tasks."))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [clientId]);

  function refetch() {
    return listClientCases(clientId)
      .then(setCases)
      .catch(() => toast.error("Couldn't load tasks."));
  }

  async function toggle(caseId: string, step: CaseStep) {
    try {
      await updateCaseStep(caseId, step.id, { is_completed: !step.isCompleted });
      await refetch();
    } catch {
      toast.error("Couldn't update task.");
    }
  }

  async function confirmDelete() {
    if (!deleting) return;
    try {
      await deleteCaseStep(deleting.caseId, deleting.step.id);
      await refetch();
    } catch {
      toast.error("Couldn't remove task.");
    } finally {
      setDeleting(null);
    }
  }

  const openCount = cases.reduce((n, c) => n + c.steps.filter((s) => !s.isCompleted).length, 0);

  return (
    <Card className="gap-0 overflow-hidden p-0">
      <div className="bg-muted/40 flex items-center justify-between border-b px-6 py-4">
        <h3 className="text-foreground flex items-center gap-2 text-lg font-bold">
          <ListTodo className="text-primary size-5" /> Tasks
          {!loading && cases.length > 0 && (
            <span className="text-muted-foreground text-sm font-normal">({openCount} open)</span>
          )}
        </h3>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <Loader />
        </div>
      ) : cases.length === 0 ? (
        <div className="text-muted-foreground grid gap-3 px-6 py-8 text-sm">
          <p>This client has no cases yet, so there are no tasks to manage.</p>
          <Button asChild variant="outline" size="sm" className="w-fit">
            <Link to={`/client/${clientId}/cases`}>Go to Cases</Link>
          </Button>
        </div>
      ) : (
        <div className="divide-y">
          {cases.map((caseRecord) => (
            <div key={caseRecord.id} className="grid gap-2 px-6 py-4">
              <div className="flex items-center justify-between">
                <h4 className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
                  {caseRecord.resolutionTypeDisplay} · {caseRecord.statusDisplay}
                </h4>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setDialog({
                      caseId: caseRecord.id,
                      step: null,
                      nextPosition: caseRecord.steps.length,
                    })
                  }
                >
                  <Plus className="size-4" /> Add Task
                </Button>
              </div>

              {caseRecord.steps.length === 0 ? (
                <p className="text-muted-foreground text-sm">No tasks in this case yet.</p>
              ) : (
                <ul className="grid gap-1">
                  {caseRecord.steps.map((step) => (
                    <li
                      key={step.id}
                      className="hover:bg-muted/40 flex items-center gap-3 rounded-md px-2 py-1.5"
                    >
                      <Checkbox
                        id={`task-${step.id}`}
                        checked={step.isCompleted}
                        onCheckedChange={() => void toggle(caseRecord.id, step)}
                      />
                      <label
                        htmlFor={`task-${step.id}`}
                        className={
                          step.isCompleted
                            ? "text-muted-foreground flex-1 text-sm line-through"
                            : "text-foreground flex-1 text-sm"
                        }
                      >
                        {step.description}
                        {step.dueDate && (
                          <span className="text-muted-foreground ml-2 text-xs">
                            due {step.dueDate}
                          </span>
                        )}
                      </label>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Edit ${step.description}`}
                        onClick={() =>
                          setDialog({ caseId: caseRecord.id, step, nextPosition: step.position })
                        }
                      >
                        <Pencil className="size-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Delete ${step.description}`}
                        onClick={() => setDeleting({ caseId: caseRecord.id, step })}
                      >
                        <Trash2 className="text-destructive size-4" />
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}

      {dialog && (
        <TaskFormDialog
          key={dialog.step?.id ?? `new-${dialog.caseId}`}
          caseId={dialog.caseId}
          step={dialog.step}
          nextPosition={dialog.nextPosition}
          open
          onOpenChange={(open) => !open && setDialog(null)}
          onSaved={refetch}
        />
      )}

      <AlertDialog open={deleting !== null} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove task?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes "{deleting?.step.description}" from the case.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
